// StockDetailPage — Full stock detail with chart + tabs
// Layout (matches attached photo):
//   - Top nav: < Back | (centered empty) | Share icon
//   - Pill tabs: Overview | Exchange rates | Savings | News
//   - Stock header (icon + name)
//   - Big price ($12,078.27) + change pill
//   - Big chart with marker dot
//   - 1D/1W/1M/3M/6M/1Y time range pills
//   - About section + Read more

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { STOCKS } from '../../data/stocks';

type Tab = 'Overview' | 'Exchange rates' | 'Savings' | 'News';
const TABS: Tab[] = ['Overview', 'Exchange rates', 'Savings', 'News'];
const RANGES = ['1D', '1W', '1M', '3M', '6M', '1Y'] as const;
type RangeKey = typeof RANGES[number];

export function StockDetailPage() {
  const { stockId } = useParams();
  const nav = useNavigate();
  const stock = useMemo(() => STOCKS.find((s) => s.id === stockId) || STOCKS[0], [stockId]);
  const [tab, setTab] = useState<Tab>('Overview');
  const [range, setRange] = useState<RangeKey>('1M');

  // Generate chart points for the selected range
  const points = useMemo(() => {
    const n = range === '1D' ? 24 : range === '1W' ? 30 : range === '1M' ? 30 : range === '3M' ? 60 : range === '6M' ? 90 : 120;
    const out: number[] = [];
    let v = stock.price * 0.85;
    for (let i = 0; i < n; i++) {
      v += (Math.sin(i * 0.3) * stock.price * 0.02) + (Math.random() - 0.5) * stock.price * 0.015;
      out.push(v);
    }
    out[out.length - 1] = stock.price;
    return out;
  }, [stock, range]);

  return (
    <div className="min-h-screen bg-white">
      {/* Top nav */}
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-white/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-text-1">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Detail</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-text-1">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" strokeLinecap="round" /></svg>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-tf-border bg-white px-3 pt-2">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`tab-${t.toLowerCase().replace(/\s/g, '-')}`}
              className={['whitespace-nowrap rounded-full px-4 py-1.5 text-[14px] font-semibold transition',
                tab === t ? 'bg-white border border-tf-text-1 text-tf-text-1' : 'text-tf-text-3 hover:bg-tf-surface-1'].join(' ')}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Price hero */}
      <div className="px-4 pt-5">
        <div className="flex items-center gap-2">
          <img src={stock.icon} alt={stock.name} className="h-6 w-6 rounded-full" />
          <h1 className="text-[20px] font-bold tracking-tight text-tf-text-1">{stock.name}</h1>
        </div>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-[36px] font-bold tracking-tight tabular-nums text-tf-text-1" data-testid="stock-price">${stock.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          <span className={['text-[15px] font-semibold', stock.change >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
            {stock.change >= 0 ? '+' : ''}{stock.change.toFixed(2)}% Today ⌄
          </span>
        </div>

        {/* Big chart with marker */}
        <div className="mt-2 -mx-2">
          <BigChart points={points} color={stock.change >= 0 ? '#007AFF' : '#FF3B30'} />
        </div>

        {/* Time range */}
        <div className="mt-2 flex items-center justify-between px-1">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              data-testid={`range-${r}`}
              className={['rounded-full px-3 py-1 text-[14px] font-semibold',
                r === range ? 'bg-tf-text-1 text-white' : 'text-tf-text-3'].join(' ')}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* About */}
      {tab === 'Overview' && (
        <div className="mt-6 px-4">
          <h2 className="text-[16px] font-semibold text-tf-text-1">About {stock.name}</h2>
          <p className="mt-2 text-[14px] leading-6 text-tf-text-3">
            {stock.description} <button className="font-semibold text-tf-blue">Read more</button>
          </p>

          <h3 className="mt-6 text-[16px] font-semibold text-tf-text-1">Key statistics</h3>
          <div className="mt-3 overflow-hidden rounded-[12px] border border-tf-border bg-white">
            <StatRow label="Market cap" value={stock.marketCap || '—'} />
            <StatRow label="Exchange" value={stock.exchange} />
            <StatRow label="Currency" value={stock.currency} last />
          </div>

          <button
            onClick={() => nav('/portal/buy/' + stock.id)}
            data-testid="stock-buy"
            className="mt-6 flex h-[52px] w-full items-center justify-center rounded-full bg-tf-text-1 text-[16px] font-semibold text-white active:scale-[0.99]"
          >
            Buy {stock.symbol}
          </button>
        </div>
      )}

      {tab !== 'Overview' && (
        <div className="px-4 pt-6">
          <div className="rounded-[16px] border border-tf-border bg-white p-6 text-center">
            <div className="text-[16px] font-semibold text-tf-text-1">{tab}</div>
            <p className="mt-2 text-[14px] text-tf-text-3">Content for this section will appear here.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function StatRow({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div className={['flex items-center justify-between px-4 py-3', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <span className="text-[14px] text-tf-text-3">{label}</span>
      <span className="text-[14px] font-medium tabular-nums text-tf-text-1">{value}</span>
    </div>
  );
}

function BigChart({ points, color = '#007AFF' }: { points: number[]; color?: string }) {
  if (points.length < 2) return null;
  const w = 360, h = 220;
  const min = Math.min(...points), max = Math.max(...points) - min || 1;
  const path = points.map((p, i) => {
    const x = (i / (points.length - 1)) * w;
    const y = h - ((p - min) / max) * (h - 30) - 15;
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
  const fillPath = `${path} L${w} ${h} L0 ${h} Z`;
  // Marker at ~80% along
  const markerIdx = Math.floor(points.length * 0.78);
  const mx = (markerIdx / (points.length - 1)) * w;
  const my = h - ((points[markerIdx] - min) / max) * (h - 30) - 15;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="h-[220px] w-full">
      <defs>
        <linearGradient id="bigchartfill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fillPath} fill="url(#bigchartfill)" />
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Marker dot */}
      <line x1={mx} y1="0" x2={mx} y2={h} stroke={color} strokeWidth="1" strokeDasharray="2 4" />
      <circle cx={mx} cy={my} r="6" fill="white" stroke={color} strokeWidth="2.5" />
      {/* Tooltip-like label */}
      <g transform={`translate(${mx + 12},${my - 24})`}>
        <rect width="80" height="22" rx="11" fill="#1C1C1E" />
        <text x="40" y="15" textAnchor="middle" fontSize="11" fontWeight="600" fill="white">+8.08%</text>
      </g>
    </svg>
  );
}
